import { BridgeComponent } from '@hotwired/hotwire-native-bridge';
import { t } from '../../javascript/lib/i18n';

/**
 * Activate the dashboard tab bar component for the native app.
 * This sends a connect message to show the tabs, which are configured below.
 * When one's tapped, it sends it `type` back, which is received and used to construct
 * a url which is visited.
 *
 * Using `data-bridge--dashboard-tabs-hide-value="true"` will hide the tabs again
 */
export default class extends BridgeComponent {
  static component = 'dashboard-tabs';
  static values = {
    dashboardUrl: String,
    defaultDashboard: String,
    hide: Boolean,
    canUseAi: Boolean,
    clinician: Boolean,
    clinicianDashboardUrl: String,
  };

  connect() {
    super.connect();
    document.removeEventListener('turbo:morph', this.reconnectAfterMorph);
    document.addEventListener('turbo:morph', this.reconnectAfterMorph);

    // Avoid cache snapshots showing incorrectly highlighted tabs
    if (document.documentElement.hasAttribute('data-turbo-preview')) return;

    // Hide the tab bar
    if (this.hideValue) {
      this.send('hide');
      return;
    }

    if (!this.clinicianValue) {
      this.url = new URL(this.dashboardUrlValue, window.location.origin);
    }
    this.selectedTab = this.clinicianValue
      ? 'dashboard'
      : this.defaultDashboardValue || 'tasks';

    // Show the tab bar
    this.send(
      'connect',
      {
        tabs: this.#tabs,
        selected: this.selectedTab,
      },
      this.#visitUrlOnTabBarNavigation
    );
  }

  disconnect() {
    super.disconnect();
    document.removeEventListener('turbo:morph', this.reconnectAfterMorph);

    const toggle = document.getElementById('amba-drawer-toggle');
    if (toggle && this.drawerCloseHandler) {
      toggle.removeEventListener('change', this.drawerCloseHandler);
    }
  }

  // A morph keeps this element, so `connect` does not re-run, but a native
  // `replace` visit creates a new screen that needs the tab bar sent again.
  // Drop the old screen's reply callbacks first; the new connect supersedes them.
  reconnectAfterMorph = () => {
    this.removePendingCallbacks();
    this.pendingMessageCallbacks = [];
    this.connect();
  };

  #visitUrlOnTabBarNavigation = (message) => {
    // Native sends back the selected tab type when user taps a tab
    if (message && message.data && message.data.type) {
      if (message.data.type === 'hey_amba') {
        this.#openDrawerAndListenForClose();
        return;
      }

      if (message.data.type === 'dashboard') {
        this.selectedTab = 'dashboard';
        window.Turbo.visit(this.clinicianDashboardUrlValue, {
          action: 'replace',
        });
        return;
      }

      this.selectedTab = message.data.type;

      const newUrl = new URL(this.url);
      newUrl.searchParams.set('dashboard_type', message.data.type);
      window.Turbo.visit(newUrl.toString(), { action: 'replace' });
    }
  };

  #openDrawerAndListenForClose() {
    document.querySelector('.amba-ai-assistant-bubble__button')?.click();

    const toggle = document.getElementById('amba-drawer-toggle');
    if (!toggle) return;

    this.drawerCloseHandler = () => {
      if (!toggle.checked) {
        toggle.removeEventListener('change', this.drawerCloseHandler);
        this.#resendSelectedTab();
      }
    };

    toggle.addEventListener('change', this.drawerCloseHandler);
  }

  #resendSelectedTab() {
    this.send(
      'connect',
      {
        tabs: this.#tabs,
        selected: this.selectedTab,
      },
      this.#visitUrlOnTabBarNavigation
    );
  }

  get #tabs() {
    if (this.clinicianValue) {
      const tabs = [
        {
          title: t('js.bridge.dashboard_tabs.dashboard'),
          type: 'dashboard',
          iosSystemImageName: 'list.bullet',
        },
      ];

      if (this.canUseAiValue) {
        tabs.push({
          title: t('js.bridge.dashboard_tabs.hey_amba'),
          type: 'hey_amba',
          iosSystemImageName: 'bubble.left.and.text.bubble.right',
        });
      }

      return tabs;
    }

    const tabs = [
      {
        title: t('js.bridge.dashboard_tabs.tasks'),
        type: 'tasks',
        iosSystemImageName: 'checklist',
      },
      {
        title: t('js.bridge.dashboard_tabs.card'),
        type: 'card',
        iosSystemImageName: 'square.grid.2x2',
      },
      {
        title: t('js.bridge.dashboard_tabs.list'),
        type: 'list',
        iosSystemImageName: 'list.bullet',
      },
    ];

    if (this.canUseAiValue) {
      tabs.push({
        title: t('js.bridge.dashboard_tabs.hey_amba'),
        type: 'hey_amba',
        iosSystemImageName: 'bubble.left.and.text.bubble.right',
      });
    }

    return tabs;
  }
}
